import LinkIcon from "@mui/icons-material/Link";
import { Alert, Snackbar } from "@mui/material";
import * as React from "react";

import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

interface CopyLinkButtonProps {
  /** URL to copy to clipboard. Must be fully qualified. */
  url: string;
  /** Tooltip text. Defaults to "Copy share link". */
  tooltip?: string;
  /** Size of the icon — small for table rows, medium for headers. */
  size?: "small" | "medium";
  /** Optional click-handler extension — fires after the copy succeeds/fails. */
  onCopied?: (ok: boolean) => void;
}

/**
 * Icon button that copies a URL to the clipboard and shows a confirmation toast.
 *
 * Distinct from ``shared/components/CopyToClipboardButton`` because it uses
 * a link icon and a Snackbar toast (vs. the shared component's content-copy
 * icon and inline tooltip change). The two visual shapes serve different
 * surfaces; the clipboard call itself is one line so dedup isn't worth a
 * shared component layer.
 */
export const CopyLinkButton: React.FC<CopyLinkButtonProps> = ({
  url,
  tooltip = "Copy share link",
  size = "small",
  onCopied,
}) => {
  const [toast, setToast] = React.useState<{
    severity: "success" | "error";
    message: string;
  } | null>(null);

  const copy = async (e: React.MouseEvent) => {
    // Don't trigger row-level onClick handlers when the icon is inside a
    // clickable parent (e.g. a MUI TableRow with onClick).
    e.stopPropagation();
    let ok: boolean;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch (err) {
      // Clipboard API rejection (denied permissions, insecure context, etc.)
      // — surface to monitoring instead of being swallowed silently. The
      // toast covers the user-visible side; ErrorMonitoringService gives
      // ops something to grep when "copy doesn't work" tickets land.
      ErrorMonitoringService.captureError(
        err instanceof Error ? err : new Error(String(err)),
        { context: { source: "CopyLinkButton" } },
      );
      ok = false;
    }
    setToast(
      ok
        ? { severity: "success", message: "Link copied" }
        : { severity: "error", message: "Couldn't copy link" },
    );
    onCopied?.(ok);
  };

  return (
    <>
      <RobotoTooltip title={tooltip}>
        <span>
          <RobotoIconButton
            onClick={(e) => void copy(e)}
            size={size}
            eventName="CopyLinkClicked"
            eventProperties={{ link: url }}
          >
            <LinkIcon fontSize="inherit" />
          </RobotoIconButton>
        </span>
      </RobotoTooltip>
      {toast !== null && (
        <Snackbar
          open
          autoHideDuration={2000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert severity={toast.severity} variant="filled">
            {toast.message}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};
